import {
  RefreshCw,
  Circle,
  CircleCheck,
  CircleAlert,
  CircleX,
  Info,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import type { Channel } from '@/types/channel';

interface ChannelHealthSectionProps {
  channel: Channel;
  qualityRating: string | null;
  onRefreshQuality?: () => void;
  refreshingQuality?: boolean;
}

function getQualityBadge(rating: string | null): { label: string; variant: 'default' | 'destructive' | 'secondary'; className?: string } {
  switch (rating) {
    case 'GREEN':
      return { label: 'High Quality', variant: 'default', className: 'bg-emerald-500 hover:bg-emerald-600' };
    case 'YELLOW':
      return { label: 'Medium Quality', variant: 'default', className: 'bg-amber-500 hover:bg-amber-600' };
    case 'RED':
      return { label: 'Low Quality', variant: 'destructive' };
    default:
      return { label: 'Not yet rated', variant: 'secondary' };
  }
}

// Icon next to "Meta WhatsApp Business Account Quality Rating" reflects the
// rating value itself, not overall channel health (forwarding/webhook state
// belongs to a separate channel-list dot — see lib/health.ts). UNKNOWN/null
// is a neutral state, NOT a problem signal — Meta hasn't rated the WABA yet.
function getQualityIcon(rating: string | null): { Icon: typeof Circle; color: string } {
  switch (rating) {
    case 'GREEN':
      return { Icon: CircleCheck, color: 'text-emerald-500' };
    case 'YELLOW':
      return { Icon: CircleAlert, color: 'text-amber-500' };
    case 'RED':
      return { Icon: CircleX, color: 'text-red-500' };
    default:
      return { Icon: Circle, color: 'text-muted-foreground' };
  }
}

export function ChannelHealthSection({
  channel,
  qualityRating,
  onRefreshQuality,
  refreshingQuality,
}: ChannelHealthSectionProps) {
  const { Icon: StatusIcon, color: statusIconColor } = getQualityIcon(qualityRating);

  const qualityBadge = getQualityBadge(qualityRating);

  const isCoexistence = channel.connectionType === 'coexistence';

  return (
    <div className="space-y-4">
      {/* Quality Rating — only for Cloud API channels */}
      {!isCoexistence && (
        <Card>
          <CardContent className="space-y-1">
            <div className="flex items-start gap-2 mb-3">
              <StatusIcon className={`size-4 shrink-0 translate-y-0.5 ${statusIconColor}`} />
              <span className="text-sm font-semibold">
                Meta WhatsApp Business Account Quality Rating
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        aria-label="What is the quality rating?"
                        className="ml-1 inline-flex translate-y-0.5 items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
                      />
                    }
                  >
                    <Info className="size-3.5" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Meta rates each WhatsApp Business phone number based on
                    how customers have received your messages over the past 7
                    days. Signals include blocks, reports, mutes, archives,
                    and the reasons users give when blocking you. A drop to
                    Medium or Low can lower your daily messaging limits.
                  </TooltipContent>
                </Tooltip>
              </span>
            </div>
            <div className="flex items-center gap-3 pl-6">
              <Badge variant={qualityBadge.variant} className={qualityBadge.className}>
                {qualityBadge.label}
              </Badge>
              {onRefreshQuality && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={onRefreshQuality}
                  disabled={refreshingQuality}
                  aria-label="Refresh quality rating"
                  title="Refresh quality rating"
                >
                  {refreshingQuality ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="size-3.5" />
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts moved to channel-detail.tsx top section */}
    </div>
  );
}
